/* ------------------------------------------------------------------
   Premium Marquee Announcement
   File: assets/premium-marquee-announcement.js

   Mobile-safe version:
   - Does not rebuild on every mobile browser UI resize.
   - Rebuilds only when the marquee width actually changes.
   - Pauses animation while cloning, forces a layout refresh, then
     resumes it so WebKit/Chrome Mobile cannot leave a stale layer.
   - Restarts after tab visibility/page restoration.
   - No jQuery, setInterval, or requestAnimationFrame animation loop.
------------------------------------------------------------------ */

(function () {
  var sections = new WeakMap();

  function getState(section) {
    var state = sections.get(section);
    if (!state) {
      state = {
        width: 0,
        resizeTimer: null,
        resizeObserver: null,
        touchTimer: null
      };
      sections.set(section, state);
    }
    return state;
  }

  function rebuildTrack(section, force) {
    var wrapper = section.querySelector('.pma-track-wrapper');
    var track = section.querySelector('.pma-track');
    if (!wrapper || !track) return;

    var state = getState(section);
    var containerWidth = Math.ceil(wrapper.getBoundingClientRect().width);
    if (!containerWidth) return;

    // Mobile Safari can fire resize while the address bar expands or
    // collapses. Do not rebuild for a height-only viewport change.
    if (!force && Math.abs(containerWidth - state.width) < 2) return;
    state.width = containerWidth;

    var groups = track.querySelectorAll('.pma-group');
    if (!groups.length) return;

    if (!track.dataset.pmaSource) {
      var source = groups[0].cloneNode(true);
      source.removeAttribute('aria-hidden');
      track.dataset.pmaSource = 'true';
      track.innerHTML = '';
      track.appendChild(source);
    }

    var sourceGroup = track.querySelector('.pma-group');
    if (!sourceGroup) return;

    var groupWidth = Math.ceil(sourceGroup.getBoundingClientRect().width);
    if (!groupWidth) return;

    var repeats = Math.max(1, Math.ceil(containerWidth / groupWidth) + 1);
    var wasPaused = track.classList.contains('is-paused');

    track.classList.add('pma-rebuilding');
    track.style.animationPlayState = 'paused';

    Array.prototype.slice.call(track.querySelectorAll('.pma-group')).forEach(function (group) {
      if (group !== sourceGroup) group.remove();
    });

    for (var i = 1; i < repeats; i += 1) {
      var firstHalfClone = sourceGroup.cloneNode(true);
      firstHalfClone.setAttribute('aria-hidden', 'true');
      track.appendChild(firstHalfClone);
    }

    for (var j = 0; j < repeats; j += 1) {
      var secondHalfClone = sourceGroup.cloneNode(true);
      secondHalfClone.setAttribute('aria-hidden', 'true');
      track.appendChild(secondHalfClone);
    }

    // Force the browser to commit the new track width before restarting
    // the compositor animation. This fixes the mobile disappear/reappear
    // behavior caused by rebuilding during an active CSS animation.
    void track.offsetWidth;
    track.style.animationName = 'none';
    void track.offsetWidth;
    track.style.animationName = '';

    window.requestAnimationFrame(function () {
      track.classList.remove('pma-rebuilding');
      track.style.animationPlayState = wasPaused ? 'paused' : 'running';
    });
  }

  function scheduleRebuild(section, force) {
    var state = getState(section);
    clearTimeout(state.resizeTimer);
    state.resizeTimer = window.setTimeout(function () {
      rebuildTrack(section, force === true);
    }, 180);
  }

  function bindResize(section) {
    var state = getState(section);
    var wrapper = section.querySelector('.pma-track-wrapper');
    if (!wrapper) return;

    if (window.ResizeObserver) {
      state.resizeObserver = new ResizeObserver(function () {
        scheduleRebuild(section, false);
      });
      state.resizeObserver.observe(wrapper);
    } else {
      var lastWidth = 0;
      window.addEventListener('resize', function () {
        var width = Math.ceil(wrapper.getBoundingClientRect().width);
        if (Math.abs(width - lastWidth) >= 2) {
          lastWidth = width;
          scheduleRebuild(section, false);
        }
      });
    }
  }

  function bindTouch(section) {
    var track = section.querySelector('.pma-track');
    if (!track || section.dataset.pauseTouch !== 'true') return;

    var state = getState(section);

    function pause() {
      clearTimeout(state.touchTimer);
      track.classList.add('is-paused');
      track.style.animationPlayState = 'paused';
    }

    function resume() {
      clearTimeout(state.touchTimer);
      state.touchTimer = window.setTimeout(function () {
        track.classList.remove('is-paused');
        track.style.animationPlayState = 'running';
      }, 80);
    }

    section.addEventListener('touchstart', pause, { passive: true });
    section.addEventListener('touchend', resume, { passive: true });
    section.addEventListener('touchcancel', resume, { passive: true });
  }

  function blockManualInput(section) {
    function preventDrag(event) {
      event.preventDefault();
    }

    section.addEventListener('wheel', preventDrag, { passive: false });
    section.addEventListener('dragstart', preventDrag, { passive: false });
  }

  function initSection(section) {
    if (section.dataset.pmaInitialized === 'true') {
      scheduleRebuild(section, true);
      return;
    }

    section.dataset.pmaInitialized = 'true';
    rebuildTrack(section, true);
    bindResize(section);
    bindTouch(section);
    blockManualInput(section);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.premium-marquee-announcement').forEach(initSection);
  }

  function restartVisibleSections() {
    document.querySelectorAll('.premium-marquee-announcement').forEach(function (section) {
      var track = section.querySelector('.pma-track');
      if (!track || track.classList.contains('is-paused')) return;
      track.style.animationPlayState = 'paused';
      void track.offsetWidth;
      track.style.animationPlayState = 'running';
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  window.addEventListener('load', function () {
    initAll(document);
    restartVisibleSections();
  });

  window.addEventListener('pageshow', restartVisibleSections);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) restartVisibleSections();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
